import { useQuery } from "@apollo/client/react"
import Button from "@mui/material/Button"
import Link from "@mui/material/Link"
import { Formik } from "formik"
import React, { useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"

import AuthLayout from "@/components/auth/auth_layout"
import ForgotPasswordModal from "@/components/auth/forgot_password_modal"
import OAuthButtons from "@/components/auth/oauth_buttons"
import { signInSchema } from "@/components/auth/validation_schemas"
import { TextField } from "@/components/form"
import { GetOAuthProvidersDocument } from "@/graphql/graphql"
import i18n from "@/i18n"
import { showErrorToast } from "@/shared/toast"
import { useStore } from "@/store"

const STYLES = {
  topNavText: "text-gray-600 text-sm",
  topNavLink: "font-medium",
  heading: "mb-8 mt-0 font-semibold text-4xl",
  form: "w-full",
  forgotPasswordLink: "mt-2",
  submitButton: "mt-2",
  errorText: "mt-4 text-sm text-red-600",
  contentContainer: "flex flex-col gap-4 min-w-100 w-full",
  fieldsContainer: "flex flex-col gap-4",
} as const

type FormValues = {
  email: string
  password: string
}

const SignInPage = () => {
  const [error, setError] = useState("")
  const navigate = useNavigate()
  const location = useLocation()
  const { setCurrentUser } = useStore()
  const { data: oauthData } = useQuery(GetOAuthProvidersDocument)

  const forgotPasswordOpen = location.pathname.replace(/\/$/, "") === "/sign_in/forgot_password"
  const closeForgotPassword = () => navigate("/sign_in")

  const googleEnabled = oauthData?.oauthProviders?.googleEnabled ?? false
  const facebookEnabled = oauthData?.oauthProviders?.facebookEnabled ?? false
  const hasOAuthProviders = googleEnabled || facebookEnabled

  const initialValues: FormValues = {
    email: "",
    password: "",
  }

  const onSubmit = async (values: FormValues, { setSubmitting }) => {
    setError("")

    try {
      const response = await fetch("/auth/sign_in", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: values.email,
          password: values.password,
        }),
        mode: "cors",
        credentials: "same-origin",
      })

      const data = await response.json()

      if (data.success) {
        setCurrentUser(data.user)
        navigate("/panel")
      } else if (data.error) {
        setError(data.error)
      } else {
        setError(i18n.t("app.auth.login.generic_error"))
      }
    } catch (err) {
      showErrorToast()
      console.error(err)
      setError(i18n.t("app.auth.login.generic_error"))
    } finally {
      setSubmitting(false)
    }
  }

  const topNav = (
    <div className={STYLES.topNavText}>
      {i18n.t("app.auth.login.no_account")}{" "}
      <Link href="/sign_up" underline="none" className={STYLES.topNavLink} data-testid="auth-signin-signup-link">
        {i18n.t("app.auth.login.sign_up")}
      </Link>
    </div>
  )

  return (
    <AuthLayout topNav={topNav} testIdPrefix="auth-signin">
      <h4 className={STYLES.heading}>{i18n.t("app.auth.login.header")}</h4>

      <div className={STYLES.contentContainer}>
        {!!hasOAuthProviders && <OAuthButtons googleEnabled={googleEnabled} facebookEnabled={facebookEnabled} />}

        <Formik
          initialValues={initialValues}
          validationSchema={signInSchema}
          onSubmit={onSubmit}
          validateAfterSubmit={true}
        >
          {({ isSubmitting, handleSubmit }) => (
            <form onSubmit={handleSubmit} className={STYLES.form} data-testid="auth-signin-form">
              <div className={STYLES.fieldsContainer}>
                <TextField
                  name="email"
                  label={i18n.t("app.auth.login.email")}
                  type="email"
                  data-testid="auth-signin-email-input"
                />

                <TextField
                  name="password"
                  label={i18n.t("app.auth.login.password")}
                  type="password"
                  data-testid="auth-signin-password-input"
                />
                <div className={STYLES.forgotPasswordLink}>
                  <Link
                    href="/sign_in/forgot_password"
                    variant="body2"
                    underline="hover"
                    onClick={(e) => {
                      e.preventDefault()
                      navigate("/sign_in/forgot_password")
                    }}
                    data-testid="auth-signin-forgot-password-link"
                  >
                    {i18n.t("app.auth.login.forgot_password")}
                  </Link>
                </div>

                {!!error && (
                  <div className={STYLES.errorText} data-testid="auth-signin-error-message">
                    {error}
                  </div>
                )}

                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  fullWidth
                  disabled={isSubmitting}
                  className={STYLES.submitButton}
                  data-testid="auth-signin-submit-btn"
                >
                  {isSubmitting ? "..." : i18n.t("app.auth.login.submit")}
                </Button>
              </div>
            </form>
          )}
        </Formik>
      </div>

      <ForgotPasswordModal open={forgotPasswordOpen} onClose={closeForgotPassword} />
    </AuthLayout>
  )
}

export default SignInPage
